//utils/saveDraftFormToFirestore.js
import { FieldValue } from 'firebase/firestore';
import pako from 'pako';
import { saveDraftFormToFirestore as firestoreSaveDraft } from './firestore'; // Firestore utility function
import { decompressData } from './decompressionUtils';

// Utility to gather form data for draft saving (in case of future use)
export const gatherDraftFormData = (formId, formDetails, layoutConfig, themeData, fieldChanges, submissionSettings, currentUser, organizationId) => {
  console.log('[utils/saveDraftFormToFirestore] Gathering form draft data...');
  console.log(`[utils/saveDraftFormToFirestore] Received organizationId: ${organizationId}`);

  const draftData = {
    formId,
    formDetails: {
      ...formDetails,
      title: formDetails.title,
      description: formDetails.description,
      createdBy: formDetails.createdBy || currentUser.uid,
      owner: formDetails.owner || currentUser.uid,
      organizationId: organizationId || '', 
      isTemplate: formDetails.isTemplate || false,
      isPublic: formDetails.isPublic || false,
    },
    layoutConfig,
    theme: themeData, // Theme data
    fieldChanges, // Field changes
    submissionSettings, // Submission settings
    status: 'draft',
    lastModified: FieldValue.serverTimestamp(), // Use Firestore server timestamp
  };
  console.log('[utils/saveDraftFormToFirestore] layoutConfig:', layoutConfig);

  console.log('[utils/saveDraftFormToFirestore] Draft Data to Save with organizationId:', draftData.formDetails.organizationId);
  console.log('[utils/saveDraftFormToFirestore] Complete Draft Data:', draftData);

  return draftData;
};

// Asynchronous utility to compress data using pako for drafts
export const compressDataForDraft = async (data) => {
  try {
    console.log('[utils/saveDraftFormToFirestore] Compressing draft form data asynchronously...');
    
    // Log the data before compression for debugging
    console.log('[utils/saveDraftFormToFirestore] Draft Data Before Compression:', data);

    // Convert the draft data to a string
    const dataString = JSON.stringify(data);

// Compress the data asynchronously using pako
const compressed = await pako.deflate(dataString);

// Convert the compressed data to Base64 for Firestore compatibility
// NEW: safer base64 encoding (avoids spread-arg limits on large payloads)
let binary = '';
const chunkSize = 0x8000; // 32KB chunks
for (let i = 0; i < compressed.length; i += chunkSize) {
  binary += String.fromCharCode.apply(null, compressed.subarray(i, i + chunkSize));
}
const base64Compressed = btoa(binary);

console.log('[utils/saveDraftFormToFirestore] Draft data compressed and encoded as Base64 successfully.');

    // Log the compressed data for debugging
    console.log('Compressed Data (Base64):', base64Compressed);

    return base64Compressed; // Return the compressed data
  } catch (error) {
    console.error('[saveDraftFormToFirestore] Error compressing draft data:', error);
    throw error; // Ensure the error is handled properly at the calling level
  }
};

// Optional: Function to compress data in chunks for very large forms
export const compressDataInChunks = async (data, chunkSize = 100000) => {
  try {
    console.log('[utils/saveDraftFormToFirestore] Compressing data in chunks...');

    // Convert data to a string
    const dataString = JSON.stringify(data);

    // Split data into chunks based on the specified chunk size
    const chunks = [];
    for (let i = 0; i < dataString.length; i += chunkSize) {
      const chunk = dataString.slice(i, i + chunkSize);

      // Compress each chunk asynchronously
      const compressedChunk = await pako.deflate(chunk);

      // Convert the compressed chunk to Base64
      const base64Chunk = btoa(String.fromCharCode(...compressedChunk));

      // Push the compressed chunk into the chunks array
      chunks.push(base64Chunk);
    }

    console.log('[saveDraftFormToFirestore] Data compressed into chunks successfully.');

    return chunks; // Return the array of compressed chunks
  } catch (error) {
    console.error('[saveDraftFormToFirestore] Error compressing data in chunks:', error);
    throw error; // Ensure the error is handled properly at the calling level
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Normalize args so this util supports BOTH calling styles:
//   A) saveDraftFormToFirestore({ ...payload })
//   B) saveDraftFormToFirestore(formId, compressedDraftData, compressedLayoutConfig, userId, organizationId, formDetails)
// Also adds `saveMode` (default: "draftOverlay") for enterprise "working copy" behavior.
// ─────────────────────────────────────────────────────────────────────────────
const normalizeDraftSaveArgs = (
  argsOrFormId,
  compressedDraftData,
  compressedLayoutConfig,
  userId,
  organizationId,
  formDetails
) => {
  // Object-style (preferred going forward)
  if (argsOrFormId && typeof argsOrFormId === 'object') {
    return {
      saveMode: 'draftOverlay', // default
      ...argsOrFormId,
    };
  }

  // Legacy positional-style (backward compatible)
  return {
    saveMode: 'draftOverlay', // default
    formId: argsOrFormId,
    compressedDraftData,
    compressedLayoutConfig,
    userId,
    organizationId,
    formDetails,
  };
};


// Function to save the compressed draft data to Firestore
export const saveDraftFormToFirestore = async (
  argsOrFormId,
  compressedDraftData,
  compressedLayoutConfig,
  userId,
  organizationId,
  formDetails
) => {
  const normalized = normalizeDraftSaveArgs(
    argsOrFormId,
    compressedDraftData,
    compressedLayoutConfig,
    userId,
    organizationId,
    formDetails
  );

  const {
    formId: normalizedFormId,
    userId: normalizedUserId,
    organizationId: normalizedOrganizationId,
    formDetails: normalizedFormDetails,

    // ✅ NEW: mode tells Firestore layer how to store it
    // - "draftOverlay" (recommended for published forms)
    // - "draft" (true draft for brand-new forms)
    saveMode = 'draftOverlay',

    // ✅ NEW: allow passing additional compressed blobs for the overlay pattern
    compressedThemeData,
    compressedFieldsMetadata,
    compressedDropdownOptions,
    compressedDropdownDependencies,
    compressedSubmissionSettings,
    // (keep accepting compressedDraftData + compressedLayoutConfig)
    compressedDraftData: normalizedCompressedDraftData = compressedDraftData,
    compressedLayoutConfig: normalizedCompressedLayoutConfig = compressedLayoutConfig,
  } = normalized;

  console.log(
    `[utils/saveDraftFormToFirestore] Attempting to save draft form ${normalizedFormId} for user ${normalizedUserId}...`
  );

  try {
    // ✅ NEW: Log which "mode" we are saving in
    console.log('[utils/saveDraftFormToFirestore] saveMode:', saveMode);

    // ✅ NEW: quick presence snapshot for debugging
    console.log('[utils/saveDraftFormToFirestore] Payload blob presence:', {
      hasCompressedDraftData: !!normalizedCompressedDraftData,
      hasCompressedLayoutConfig: !!normalizedCompressedLayoutConfig,
      hasCompressedThemeData: !!compressedThemeData,
      hasCompressedFieldsMetadata: !!compressedFieldsMetadata,
      hasCompressedDropdownOptions: !!compressedDropdownOptions,
      hasCompressedDropdownDependencies: !!compressedDropdownDependencies,
      hasCompressedSubmissionSettings: !!compressedSubmissionSettings,
    });

    // Check if formDetails is defined before accessing its properties
    if (!normalizedFormDetails) {
      console.error(
        '[utils/saveDraftFormToFirestore] formDetails is undefined! This may be the root cause of the error.'
      );
    } else {
      // Log form details to ensure they are populated correctly
      console.log('[utils/saveDraftFormToFirestore] formDetails object:', normalizedFormDetails);

      // Log the title and description to ensure they are not undefined
      if (!normalizedFormDetails.title) {
        console.error('[utils/saveDraftFormToFirestore] Title is undefined or missing.');
      } else {
        console.log('[utils/saveDraftFormToFirestore] Form Title:', normalizedFormDetails.title);
      }

      if (!normalizedFormDetails.description) {
        console.error('[saveDraftFormToFirestore] Description is undefined or missing.');
      } else {
        console.log('[utils/saveDraftFormToFirestore] Form Description:', normalizedFormDetails.description);
      }
    }

    // Log organizationId to ensure it is passed correctly
    console.log(
      '[utils/saveDraftFormToFirestore] OrganizationId before passing to firestoreSaveDraft:',
      normalizedOrganizationId || 'No OrganizationId provided.'
    );

    // Log compressed data to ensure it’s prepared properly
    console.log('[utils/saveDraftFormToFirestore] Compressed Draft Data to Save:', normalizedCompressedDraftData);

    // Log userId to track which user is saving the draft
    console.log('[utils/saveDraftFormToFirestore] User ID:', normalizedUserId);

    // Pass the relevant details to Firestore
    console.log('[utils/saveDraftFormToFirestore] Calling firestoreSaveDraft with the following data:');
    console.log('Form ID:', normalizedFormId);
    console.log('Compressed Data:', normalizedCompressedDraftData);
    console.log('User ID:', normalizedUserId);
    console.log('Organization ID:', normalizedOrganizationId);
    console.log('Form Details (Title, Description):', normalizedFormDetails);

    //  NEW: Prefer object-style call (works best with the upcoming Firestore overlay changes)
    // Fallback to legacy positional signature to avoid breaking older code.
    let saveResult;

    try {
      saveResult = await firestoreSaveDraft({
        formId: normalizedFormId,
        compressedDraftData: normalizedCompressedDraftData,
        compressedLayoutConfig: normalizedCompressedLayoutConfig,
        compressedThemeData,
        compressedFieldsMetadata,
        compressedDropdownOptions,
        compressedDropdownDependencies,
        compressedSubmissionSettings,
        userId: normalizedUserId,
        organizationId: normalizedOrganizationId,
        formDetails: normalizedFormDetails,
        saveMode,
      });
    } catch (e) {
      console.warn(
        '[utils/saveDraftFormToFirestore] firestoreSaveDraft object-call failed; retrying legacy positional signature (non-fatal if it succeeds):',
        e?.message || e
      );

      saveResult = await firestoreSaveDraft(
        normalizedFormId,
        normalizedCompressedDraftData,
        normalizedCompressedLayoutConfig,
        normalizedUserId,
        normalizedOrganizationId,
        normalizedFormDetails
      );
    }

    // Success log after saving the draft
    console.log(`[utils/saveDraftFormToFirestore] Form ${normalizedFormId} saved successfully.`);
    console.log(
      '[utils/saveDraftFormToFirestore] firestoreSaveDraft result:',
      saveResult
    );

    if (!saveResult || saveResult.status !== 'success') {
      console.warn(
        '[utils/saveDraftFormToFirestore] Draft save completed but returned a non-success result:',
        saveResult
      );

      return saveResult || {
        status: 'error',
        message: 'Draft save completed without a structured result.',
      };
    }

    return saveResult;
  } catch (error) {
    // Log the error in case something goes wrong
    console.error(`[saveDraftFormToFirestore] Error saving draft form ${normalizedFormId}:`, error);

    return {
      status: 'error',
      message: `Failed to save draft form: ${error.message}`,
      error,
    };
  }
};



// Temporarily bypass compression for testing
// Replace this in your logic where needed to bypass compression and confirm the issue
export const saveDraftFormWithoutCompression = async (formId, draftData, userId) => {
  console.log('[saveDraftFormWithoutCompression] Saving draft form without compression for testing...');

  try {
    // Log raw data before saving to Firestore for debugging
    console.log('[saveDraftFormWithoutCompression] Draft Data to Save:', draftData);

    // SAFETY GUARD:
    // Your Firestore "draft save" path expects COMPRESSED base64 strings (compressedDraftData, compressedLayoutConfig, etc.)
    // If we push raw objects here, we can corrupt the persisted draft shape.
    //
    // So: only allow this helper to call Firestore if the caller passes a pre-compressed string.
    if (typeof draftData !== 'string') {
      console.warn(
        '[saveDraftFormWithoutCompression] Refusing to write raw (uncompressed) draftData to Firestore.',
        'Expected a base64 compressed string. Use compressDataForDraft(...) + saveDraftFormToFirestore(...) instead.'
      );
      return;
    }

    // If (and only if) draftData is already a compressed base64 string, pass it through.
    // We intentionally use the most compatible legacy signature here.
    await firestoreSaveDraft(formId, draftData, null, userId, null, null);

    console.log(`[saveDraftFormWithoutCompression] Form ${formId} saved successfully (pre-compressed string passthrough).`);
  } catch (error) {
    console.error(`[saveDraftFormWithoutCompression] Error saving draft form ${formId} without compression:`, error);
  }
};


// Function to decompress base64-encoded and pako-compressed data
export const decompressDataForDraft = async (compressedBase64, key, persist = true) => {
  try {
    if (!compressedBase64) {
      console.warn(`[decompressDataForDraft] No compressed data for ${key}`);
      return null;
    }

    console.log(`[decompressDataForDraft] Decompressing data for: ${key}`);

    // reuse shared decompressor (supports url-safe base64, padding fixes, raw JSON)
    const parsedData = await decompressData(compressedBase64);

    console.log(`[decompressDataForDraft] Successfully decompressed data for: ${key}`);

    // Optionally store in sessionStorage
    if (persist) {
      sessionStorage.setItem(key, JSON.stringify(parsedData));
      console.log(`[decompressDataForDraft] Stored ${key} in sessionStorage.`);
    }

    return parsedData;

  } catch (error) {
    console.error(`[decompressDataForDraft] Failed to decompress ${key}:`, error);
    throw error;
  }
};

